import _ from 'lodash'
import {CustomFieldComponent} from "module/CustomField/component/CustomFieldComponent"
import {FormInputBorder} from 'module/CustomField/component/FormInputBorder'
import {CF} from "module/CustomField/util/CustomField"
import React from 'react'
import {BSAttr} from 'module/BSEntry/BSAttr';
import {CustomFieldMembersComponentProps, ValidationResult} from 'module/CustomField/type/CFComponentType';
import {CFMember, CFValue, isEqualCFValue} from 'module/CustomField/type/CFType';
import {SisName} from 'util/SisName';
import {AddCFRowButton} from 'module/CustomField/component/button/AddCFRowButton';
import {ClearCFRowsButton} from 'module/CustomField/component/button/ClearCFRowsButton';
import {CustomFieldTO} from 'generated/JavaTO'
import {cssCFLabelSticky, cssCFSubLabelSticky} from 'module/CustomField/util/cssCFLabelStickyStyles'
import {css} from '@emotion/react'

const cssField = css`
	display: grid;
	grid-template-columns: auto min-content;

	& > * {
		min-width: 0; // fixes react-select or others overflows cell
	}
`

function filterCFErrors(validationResult: ValidationResult, cfDef: CustomFieldTO, pos?: number) {
	if (!validationResult) return []
	const hasPos = typeof pos === 'number'
	return _.uniq(
		[...(validationResult[cfDef.id] || []), ...(validationResult[cfDef.dstField] || [])]
			.filter(x => !hasPos || x.pos == null || x.pos === pos)
			.map((x) => x.message),
	)
}

export const CustomFieldMembersComponent = React.memo<CustomFieldMembersComponentProps>(({
	model, persistedModel, cfDef, group, pos, disabled, validationResult,
	setCFValue, deleteCFValue, fetchCFAttribute, validateForm, hideArrayButtons, labelDone, labelHidden, inlineLabel,
}) => {
	const isCFNamespace = CF.isCFNamespace(cfDef)
	const v = CF.getValueFrom(cfDef, model, {pos})
	let cfMembers = !Array.isArray(v) ? [v] : v
	const positions = CF.getPositions([cfDef], model)
	const cfMembersLen = positions.length
	let showAddButton = false
	let showCleanButton = false
	let posNext: number | undefined;
	if (isCFNamespace && !hideArrayButtons) {
		if (!disabled && (!cfDef.maxAmount || cfDef.maxAmount > cfMembersLen)) {
			showAddButton = true
			posNext = CF.getNextPos([cfDef], model)
		}
		if (!disabled && cfDef.minAmount < cfMembersLen) {
			showCleanButton = true
		}
	}
	if (!model) return null;
	const arrayButtonVisible = !hideArrayButtons && (showAddButton || showCleanButton)
	return (
		<div
			style={{
				gridRow: `span ${cfDef.rowSpan == null ? 1 : cfDef.rowSpan}`,
				gridColumn: `span ${cfDef.colSpan == null ? 1 : cfDef.colSpan}`,
			}}
			className={'CustomFieldMembersComponent'}
		>
			<FormInputBorder
				label={labelDone || labelHidden ? undefined : cfDef.label}
				cssLabel={group?.heading || group?.mergeAmountButtons ? cssCFSubLabelSticky : cfDef.maxAmount > 1 ? cssCFLabelSticky : undefined}
				afterLabel={arrayButtonVisible && <div>
					{showAddButton && <AddCFRowButton onClick={() => setCFValue({cfDef, value: null, options: {pos: posNext}})}/>}
					{showCleanButton && <ClearCFRowsButton onClick={() => deleteCFValue?.({cfDefs: [cfDef], clearNullValues: true})}/>}
				</div>}
				required={cfDef.required}
				inline={inlineLabel}
				validationResult={filterCFErrors(validationResult, cfDef, pos)}
			>
				{cfMembers.map((currVal, i) => {
					let posMember: number | undefined
					if (typeof pos === 'number') {
						posMember = pos
					} else {
						posMember = isCFNamespace && currVal !== null ? (currVal as CFMember).pos : undefined
					}
					const modelValue = CF.getValueFrom(cfDef, model, {pos: posMember, forView: true}) as CFMember | CFValue
					const persistedValue = CF.getValueFrom(cfDef, persistedModel, {pos: posMember, forView: true}) as CFMember | CFValue
					const hasChanged = persistedValue && !isEqualCFValue(modelValue, persistedValue) || undefined
					return (
						<div
							key={i}
							css={cssField}
							className={'CustomFieldMember'}
							style={inlineLabel ? {display: 'inline-block'} : undefined}
							data-cfdefid={cfDef.id}
							data-cfdefpos={pos}
						>
							<CustomFieldComponent
								placeholderHTML={labelHidden ? cfDef.label : undefined}
								cfDef={cfDef}
								pos={posMember}
								model={model}
								modelValue={modelValue}
								setCb={(val: CFMember | CFValue) => {
									setCFValue({cfDef, value: val, options: {pos: posMember, userInput: true}})
									if (validateForm) validateForm({checkRemoteCollision: false})
								}}
								removeCb={() => {
									deleteCFValue?.({cfDefs: [cfDef], pos: posMember});
									if (validateForm) validateForm({checkRemoteCollision: false})
								}}
								resetCb={hasChanged && (() => {
									setCFValue({cfDef, value: CF.getValueFrom(cfDef, persistedModel, {pos: posMember}) as CFMember | CFValue})
									if (validateForm) validateForm({checkRemoteCollision: false})
								})}
								fetchCb={fetchMode => {
									const id = BSAttr.getId(persistedModel)
									const sisName = persistedModel?.sisName as SisName.Booking | SisName.Persurl
									if (!fetchCFAttribute || typeof id !== 'number' || !sisName || typeof posMember !== 'number') return;
									fetchCFAttribute({
										sisName,
										id,
										dstField: CF.getCFNamespaceField(cfDef),
										pos: posMember,
										fetchMode,
									})
								}}
								disabled={disabled}
							/>
						</div>
					)
				})}
			</FormInputBorder>
		</div>
	)
})